"use client";

import { subscribeAction } from "@/actions/get-started-action";
import { useRequestCallbackModalParams } from "@/hooks/request-callback-modal-params";
import { Button } from "@se/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@se/ui/components/dialog";
import { Input } from "@se/ui/components/input";
import { Label } from "@se/ui/components/label";
import { cn } from "@se/ui/lib/utils";
import {
  Building2,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Phone,
  User,
  Zap,
} from "lucide-react";
import { useAction } from "next-safe-action/hooks";
import { useEffect, useState } from "react";

export function RequestCallbackModal() {
  const { params, setParams } = useRequestCallbackModalParams();
  const { execute, status, result } = useAction(subscribeAction);
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    mobile: "",
  });

  const isPending = status === "executing";

  useEffect(() => {
    if (result?.data && "success" in result.data && result.data.success) {
      setShowSuccess(true);
      setCountdown(30);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setParams({ isOpen: false, startingPoint: undefined });
            setTimeout(() => {
              setShowSuccess(false);
              setFormData({ name: "", email: "", company: "", mobile: "" });
            }, 300);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [result?.data, setParams]);

  const handleSubmit = async (formData: FormData) => {
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      company: formData.get("company") as string,
      mobile: formData.get("mobile") as string,
      source: params.startingPoint || undefined,
    };

    setFormData(data);
    await execute(data);
  };

  const handleOpenChange = (open: boolean) => {
    setParams({ isOpen: open, startingPoint: params.startingPoint });
    if (!open) {
      // Reset form when closing
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ name: "", email: "", company: "", mobile: "" });
      }, 300);
    }
  };

  return (
    <Dialog open={params.isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {showSuccess ? (
          <div className="py-8 text-center space-y-6 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
            <div className="relative">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto animate-in zoom-in-50 duration-500" />
              <div className="absolute -bottom-2 -right-2 bg-primary/10 rounded-full p-2">
                <Phone className="h-4 w-4 text-primary animate-pulse" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-xl">
                Thank you, {formData.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                We've received your request. A member of our team will contact
                {formData.company ? ` ${formData.company}` : " you"} within one
                business day to discuss how we can help transform your energy
                operations.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  Closing in {countdown} second{countdown !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-1000 ease-linear"
                  style={{ width: `${(countdown / 30) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 max-w-xs mx-auto space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                What happens next:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>Your details have been passed to our team</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>You'll receive a call within one business day</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>We'll discuss how we can support your business</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-primary/10 rounded-full p-1.5">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                <DialogTitle className="text-xl">
                  Request a Consultation
                </DialogTitle>
              </div>
              <DialogDescription className="text-sm leading-relaxed">
                Our energy specialists are ready to help transform your
                business. We'll contact you within one business day to arrange a
                convenient time to discuss.
              </DialogDescription>
            </DialogHeader>

            <form action={handleSubmit} className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium flex items-center gap-1"
                  >
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Smith"
                    required
                    disabled={isPending}
                    className="h-10 transition-colors focus:border-primary"
                    autoComplete="name"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="company"
                    className="text-sm font-medium flex items-center gap-1"
                  >
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder="ABC Energy Ltd"
                    required
                    disabled={isPending}
                    className="h-10 transition-colors focus:border-primary"
                    autoComplete="organization"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium flex items-center gap-1"
                >
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  Business Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john.smith@abcenergy.co.uk"
                  required
                  disabled={isPending}
                  className="h-10 transition-colors focus:border-primary"
                  autoComplete="work email"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="mobile"
                  className="text-sm font-medium flex items-center gap-1"
                >
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  Mobile Number
                </Label>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  placeholder="07700 900000"
                  pattern="[0-9]{11}"
                  title="Please enter a valid UK mobile number"
                  required
                  disabled={isPending}
                  className="h-10 transition-colors focus:border-primary"
                  autoComplete="tel"
                />
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  className="w-full h-11 font-medium transition-all hover:shadow-md"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <Phone className="mr-2 h-4 w-4" />
                      Request Consultation Call
                    </>
                  )}
                </Button>
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    No obligation
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Response within 1 business day
                  </span>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  By submitting this form, you agree to our
                  <a
                    href="/legal/privacy-policy"
                    className="underline underline-offset-2 hover:text-foreground transition-colors mx-1"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
